import last from 'lodash/last';

import { isHotKey } from '@lumapps/utils/browser/isHotKey';
import { onKey } from '@lumapps/utils/event/onKey';
import { createParagraph } from '@lumapps/wrex-typography/utils/createParagraph';
import { Editor, Element, Path, Transforms } from '@lumapps/wrex/slate';
import { findParent } from '@lumapps/wrex/slate/utils/findParent';
import { isOnFirstLineOfBlock } from '@lumapps/wrex/slate/utils/isOnFirstLineOfBlock';
import { isOnLastLineOfBlock } from '@lumapps/wrex/slate/utils/isOnLastLineOfBlock';
import type { WrexEditor } from '@lumapps/wrex/types';

import type { ColumnsEditor } from '../../types';
import { isColumn } from '../../utils/isColumn';
import { isColumns } from '../../utils/isColumns';

/**
 * Checks the exit of last column from the bottom of it.
 * It returns true only if the cursor is on the last LINE of the last BLOCK child of the LAST column.
 * @param editor
 * @returns Returns whether if we are navigating out of a column with the down arrow.
 */
export const isExitingLastColumnFromBottom = (editor: WrexEditor<ColumnsEditor>) => (event: KeyboardEvent) => {
    // If selection is not defined, we don't want to apply the effect
    if (!editor.selection) {
        return false;
    }
    const { focus } = editor.selection;
    const [parentColumnEntry, parentColumnPath] = findParent(editor, focus.path, isColumn) || [];
    if (!parentColumnEntry) {
        return false;
    }
    const [grandParentEntry] = findParent(editor, parentColumnPath, isColumns) || [];
    if (!grandParentEntry) {
        return false;
    }

    const isArrowDownPressed = isHotKey('ArrowDown')(event);
    // If the pressed key is not the ArrowDown and we're in the last column, we don't want to apply the effect
    if (!isArrowDownPressed) {
        return false;
    }

    // We get the current block at selection
    const [currentBlockEntry] = Array.from(
        Editor.nodes(editor, {
            at: editor.selection,
            match: (nd) => Element.isElement(nd) && Editor.isBlock(editor, nd),
            mode: 'lowest',
        }),
    );

    const isLastColumn = grandParentEntry.children.length - 1 === last(parentColumnPath);
    // if we are not on last column of columns we return false
    if (!isLastColumn) {
        return false;
    }

    const isLastBlock = parentColumnEntry.children.length - 1 === last(currentBlockEntry[1]);
    const isLastLine = isOnLastLineOfBlock(editor, currentBlockEntry[0]);

    return !isLastBlock ? false : isLastLine;
};

/**
 * Checks the exit of first column from the top of it.
 * It returns true only if the cursor is on the first LINE of the first BLOCK child of the FIRST column.
 * @param editor
 * @returns Returns whether if we are navigating out of a cell with the up arrow.
 */
export const isExitingFirstColumnFromTop = (editor: WrexEditor<ColumnsEditor>) => (event: KeyboardEvent) => {
    // If selection is not defined, we don't want to apply the effect
    if (!editor.selection) {
        return false;
    }
    const { focus } = editor.selection;

    const [parentColumnEntry, parentColumnPath] = findParent(editor, focus.path, isColumn) || [];
    if (!parentColumnEntry) {
        return false;
    }

    const [grandParentEntry] = findParent(editor, parentColumnPath, isColumns) || [];
    if (!grandParentEntry) {
        return false;
    }

    // If the pressed key is not the ArrowUp, and we're in the first column, we don't want to apply the effect
    const isArrowUpPressed = isHotKey('ArrowUp')(event);
    if (!isArrowUpPressed) {
        return false;
    }
    // We get the current block at selection
    const [currentBlockEntry] = Array.from(
        Editor.nodes(editor, {
            at: editor.selection,
            match: (nd) => Element.isElement(nd) && Editor.isBlock(editor, nd),
            mode: 'lowest',
        }),
    );

    const isFirstColumn = last(parentColumnPath) === 0;

    // if we are not on last column of columns we return false
    if (!isFirstColumn) {
        return false;
    }

    const isFirstBlock = last(currentBlockEntry[1]) === 0;
    const isFirstLine = isOnFirstLineOfBlock(editor, currentBlockEntry[0]);

    return !isFirstBlock ? false : isFirstLine;
};

/**
 * Initialize key event handler for the columns editor.
 *
 * @param editor - The editor
 */
export const getKeyHandler = (editor: WrexEditor<ColumnsEditor>) => {
    return onKey(
        [
            {
                match: isExitingLastColumnFromBottom(editor),
                handler: () => {
                    if (!editor.selection) {
                        return;
                    }
                    const { focus } = editor.selection;
                    // We get the columns parent at the current selection
                    const [, parentColumnsPath] = findParent(editor, focus.path, isColumns) || [];

                    if (!parentColumnsPath) {
                        return;
                    }

                    const newTargetPoint = Editor.after(editor, parentColumnsPath, {
                        distance: 1,
                        unit: 'block',
                    });

                    // if newTargetPoint we move the cursor out of the columns (after)
                    if (newTargetPoint) {
                        Transforms.select(editor, {
                            anchor: newTargetPoint,
                            focus: newTargetPoint,
                        });
                    } else {
                        // else we create a new paragraph after the current node path
                        const newLinePath = Path.next(focus.path.slice(0, 3));
                        Transforms.insertNodes(editor, createParagraph(), { at: newLinePath });
                        Transforms.select(editor, newLinePath);
                    }
                },
            },
            {
                match: isExitingFirstColumnFromTop(editor),
                handler: () => {
                    if (!editor.selection) {
                        return;
                    }
                    const { focus } = editor.selection;
                    // We get the columns parent at the current selection
                    const [, parentColumnsPath] = findParent(editor, focus.path, isColumns) || [];

                    if (!parentColumnsPath) {
                        return;
                    }

                    const newTargetPoint = Editor.before(editor, parentColumnsPath, {
                        distance: 1,
                        unit: 'block',
                    });

                    // if newTargetPoint we move the cursor out of the columns (before)
                    if (newTargetPoint) {
                        Transforms.select(editor, {
                            anchor: newTargetPoint,
                            focus: newTargetPoint,
                        });
                    } else {
                        // else we create a new paragraph before the first column
                        Transforms.insertNodes(editor, createParagraph(), { at: [0] });
                        Transforms.select(editor, [0]);
                    }
                },
            },
        ],
        { preventDefault: true },
    );
};
